// src/components/common/Sidebar.js 
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaHome, FaCalendarAlt, FaTools, FaBuilding, FaUtensils, FaThLarge, FaUsers, FaEye, FaPlus, FaSignInAlt, FaIdCard } from 'react-icons/fa';
import { FaArrowRightFromBracket as FaLogout } from 'react-icons/fa6';
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";


// Dashboard navigation items (dynamic based on role)
const getNavItems = (userRole) => {
    const commonItems = [
        { title: 'Settings', path: '/settings', icon: FaTools }
        // { title: 'Help', path: '/help', icon: FaQuestionCircle },
    ];

    if (userRole === 'admin') {
        return [
            { title: 'Home', path: '/homepage', icon: FaHome },
            { title: 'Dashboard', path: '/admin/dashboard', icon: FaThLarge },
            { title: 'Approve Listings', path: '/admin/listings', icon: FaEye },
            { title: 'Approve Owner Profiles', path: '/admin/owners', icon: FaIdCard },
            { title: 'User Management', path: '/admin/users', icon: FaUsers },
            { title: 'Bookings Management', path: '/admin/bookings', icon: FaCalendarAlt },
        ];
    }

    if (userRole === 'owner') {
        return [
            { title: 'Home', path: '/homepage', icon: FaHome },
            { title: 'My Rooms', path: '/owner/rooms', icon: FaBuilding },
            { title: 'My Mess', path: '/owner/mess', icon: FaUtensils },
            { title: 'Bookings', path: '/dashboard/bookings', icon: FaCalendarAlt },
            { title: 'Create Room', path: '/dashboard/create-room', icon: FaPlus },
            { title: 'Create Mess', path: '/dashboard/create-mess', icon: FaUtensils },
            ...commonItems,
        ];
    }

    // Guest User (no role/undefined)
    if (!userRole || userRole === 'guest') {
        return [
            { title: 'Home', path: '/homepage', icon: FaHome },
            { title: 'Search Rooms', path: '/rooms', icon: FaBuilding },
            { title: 'Search Mess', path: '/mess', icon: FaUtensils },
        ];
    }

    // Student/Default (logged in student)
    return [
        { title: 'Home', path: '/homepage', icon: FaHome },
        { title: 'Search Rooms', path: '/rooms', icon: FaBuilding },
        { title: 'Search Mess', path: '/mess', icon: FaUtensils },
        { title: 'My Bookings', path: '/student/bookings', icon: FaCalendarAlt },
        ...commonItems,
    ];
};

const Sidebar = ({ userRole, handleLogout }) => {
    const location = useLocation();
    const navItems = getNavItems(userRole);

    // Common navigation items rendering
    const renderNavItems = () => (
        <>
            {navItems.map((item, index) => (
                <Link
                    to={item.path}
                    key={item.title}
                    className={cn(
                        "flex items-center gap-3 p-3 rounded-lg transition-all duration-300 transform",
                        "hover:scale-100 hover:bg-sidebar-accent/80 hover:shadow-md",
                        "active:scale-95",
                        location.pathname === item.path
                            ? "bg-sidebar-accent shadow-lg scale-100"
                            : "hover:bg-sidebar-accent/50"
                    )}
                >
                    <item.icon className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
                    <span className="font-medium transition-all duration-300 group-hover:font-semibold">
                        {item.title}
                    </span>
                </Link>
            ))}
        </>
    );

    // Common footer section
    const renderFooter = () => (
        <div className="flex-shrink-0 pt-4 border-t border-green-700/50">
            {userRole ? (
                // Logged in user
                <>
                    <p className="text-xs text-green-400 mb-2 transition-all duration-300 hover:text-green-300">
                        {userRole.toUpperCase()} Control
                    </p>
                    <Button
                        variant="ghost"
                        onClick={handleLogout}
                        className="flex w-full justify-start gap-3 p-3 rounded-lg text-red-300 hover:bg-red-900/50 hover:text-red-200"
                    >
                        <FaLogout className="w-5 h-5" />
                        <span className="font-medium">
                            Logout
                        </span>
                    </Button>
                </>
            ) : (
                // Guest user
                <>
                    <p className="text-xs text-green-400 mb-2 transition-all duration-300 hover:text-green-300">
                        Guest Access
                    </p>
                    <Button
                        asChild
                        variant="ghost"
                        className="flex w-full justify-start gap-3 p-3 rounded-lg text-green-300 hover:bg-green-900/50 hover:text-green-200"
                    >
                        <Link to="/login">
                            <FaSignInAlt className="w-5 h-5" />
                            <span className="font-medium">
                                Login / Signup
                            </span>
                        </Link>
                    </Button>
                </>
            )}
        </div>
    );

    return (
        <div className="flex flex-col h-full p-4">
            <div className="flex-shrink-0">
                <h2 className="text-xl font-bold mb-8 tracking-wider text-green-300 transition-all duration-300 hover:scale-100 hover:text-green-200 cursor-default">
                    Dorm & Dish
                </h2>
            </div>

            <nav className="flex-1 space-y-2 overflow-hidden hover:overflow-y-auto">
                {renderNavItems()}
            </nav>

            {renderFooter()}
        </div>
    );
};

export default Sidebar;